/**
 * UI primitives - section-aware token wrappers.
 */
import type {
  ReactNode,
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'lucide-react';
import { SECTIONS, type SectionKey } from './sections';

const cls = (...parts: (string | false | undefined | null)[]): string =>
  parts.filter(Boolean).join(' ');

export const Card: React.FC<{ children: ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div
    className={cls(
      'app-shell-surface rounded-[26px] border border-border p-4 shadow-card backdrop-blur-xl',
      className,
    )}
  >
    {children}
  </div>
);

export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: 'primary' | 'ghost' | 'danger' | 'secondary';
    size?: 'sm' | 'md';
  }
>(({ variant = 'primary', size = 'md', className, ...props }, ref) => {
  const variants = {
    primary:
      'app-primary-button',
    secondary:
      'border border-border bg-surface text-ink shadow-sm backdrop-blur hover:bg-surface-raised disabled:text-ink-muted',
    ghost:
      'border border-border bg-transparent text-ink hover:bg-surface-raised disabled:text-ink-muted',
    danger:
      'bg-neg text-white shadow-[0_14px_28px_-18px_rgba(207,59,46,0.48)] hover:bg-red-700 active:bg-red-800 disabled:bg-ink-muted/30 disabled:text-ink-muted',
  };
  const sizes = {
    sm: 'min-h-[46px] rounded-[15px] px-4 py-2.5 text-small gap-1.5',
    md: 'min-h-[50px] rounded-[17px] px-4 py-3 text-body gap-2',
  };
  return (
    <button
      ref={ref}
      className={cls(
        'inline-flex items-center justify-center font-semibold transition-all duration-150 hover:-translate-y-px disabled:cursor-not-allowed disabled:hover:translate-y-0',
        sizes[size],
        variants[variant],
        className,
      )}
      {...props}
    />
  );
});
Button.displayName = 'Button';

export const TextInput = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & {
    label?: string;
    hint?: string;
    error?: string | null;
  }
>(({ label, hint, error, className, id, ...props }, ref) => {
  const inputId = id ?? props.name;
  return (
    <label className="block space-y-1.5" htmlFor={inputId}>
      {label && <span className="text-small font-semibold text-ink">{label}</span>}
      <input
        ref={ref}
        id={inputId}
        className={cls(
          'app-input-surface w-full min-h-[48px] rounded-[16px] border border-border px-3.5 py-2.5 text-body text-ink shadow-sm backdrop-blur placeholder:text-ink-muted transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20',
          error && 'border-neg focus:border-neg focus:ring-neg/20',
          className,
        )}
        {...props}
      />
      {hint && !error && <span className="text-caption text-ink-muted">{hint}</span>}
      {error && <span className="text-caption text-neg">{error}</span>}
    </label>
  );
});
TextInput.displayName = 'TextInput';

export const TextArea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: string; hint?: string }
>(({ label, hint, className, ...props }, ref) => (
  <label className="block space-y-1.5">
    {label && <span className="text-small font-semibold text-ink">{label}</span>}
    <textarea
      ref={ref}
      className={cls(
        'app-input-surface w-full rounded-[16px] border border-border px-3.5 py-2.5 text-body text-ink shadow-sm backdrop-blur placeholder:text-ink-muted transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20',
        className,
      )}
      {...props}
    />
    {hint && <span className="text-caption text-ink-muted">{hint}</span>}
  </label>
));
TextArea.displayName = 'TextArea';

export const Select: React.FC<{
  label?: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}> = ({ label, hint, value, onChange, options, placeholder }) => (
  <label className="block space-y-1.5">
    {label && <span className="text-small font-semibold text-ink">{label}</span>}
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="app-input-surface w-full min-h-[48px] rounded-[16px] border border-border px-3.5 py-2.5 text-body text-ink shadow-sm backdrop-blur transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
    {hint && <span className="text-caption text-ink-muted">{hint}</span>}
  </label>
);

export const Checkbox: React.FC<{
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}> = ({ label, hint, checked, onChange }) => (
  <label className="app-shell-surface flex min-h-[46px] cursor-pointer items-center gap-3 rounded-[16px] border border-border px-3 py-2.5 text-body shadow-sm backdrop-blur">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="h-4 w-4 rounded border-border bg-surface text-brand focus:ring-brand/30"
    />
    <span>
      <span className="text-ink">{label}</span>
      {hint && <span className="block text-caption text-ink-muted">{hint}</span>}
    </span>
  </label>
);

export const Spinner: React.FC = () => (
  <span
    aria-hidden
    className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-border border-t-brand"
  />
);

export const Pill: React.FC<{
  children: ReactNode;
  tone?: 'neutral' | 'green' | 'amber' | 'red' | 'indigo';
  section?: SectionKey;
}> = ({ children, tone = 'neutral', section }) => {
  const tones = {
    neutral: 'border border-border bg-surface-raised text-ink-muted',
    green: 'border border-emerald-200 bg-emerald-50 text-emerald-700',
    amber: 'border border-amber-200 bg-amber-50 text-amber-700',
    red: 'border border-red-200 bg-red-50 text-neg',
    indigo: 'border border-brand/20 bg-brand-light text-brand',
  };
  const sec = section ? SECTIONS[section] : null;
  return (
    <span
      className={cls(
        'inline-flex items-center rounded-full px-2.5 py-1 text-caption font-semibold uppercase tracking-[0.1em]',
        sec ? 'border' : tones[tone],
      )}
      style={sec ? { background: sec.plate, color: sec.color, borderColor: sec.edge } : undefined}
    >
      {children}
    </span>
  );
};

export const ErrorBanner: React.FC<{ children: ReactNode }> = ({ children }) => (
  <div className="rounded-[18px] border border-red-200 bg-red-50/92 p-3 text-body text-neg shadow-sm backdrop-blur">
    {children}
  </div>
);

export const EmptyState: React.FC<{
  icon: React.ComponentType<{ className?: string; strokeWidth?: string | number }>;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
  section?: SectionKey;
}> = ({ icon: Icon, title, description, action, className, section }) => (
  <div
    className={cls(
      'app-shell-surface-soft flex flex-col items-center justify-center gap-2.5 rounded-[22px] border border-dashed border-border px-6 py-8 text-center backdrop-blur',
      className,
    )}
  >
    {section ? (
      <span style={{ color: SECTIONS[section].color, opacity: 0.9 }}>
        <Icon className="h-9 w-9" strokeWidth={1.5} />
      </span>
    ) : (
      <Icon className="h-9 w-9 text-ink-muted opacity-50" strokeWidth={1.5} />
    )}
    <div>
      <div className="text-h2 text-ink">{title}</div>
      {description && <div className="mt-1 max-w-sm text-small text-ink-muted">{description}</div>}
    </div>
    {action && <div className="mt-2">{action}</div>}
  </div>
);

export interface OverflowMenuItem {
  label: string;
  icon?: React.ComponentType<{ className?: string; strokeWidth?: string | number }>;
  onClick?: () => void;
  href?: string;
  download?: string;
  target?: string;
  danger?: boolean;
  disabled?: boolean;
  separator?: boolean;
}

export const OverflowMenu: React.FC<{
  items: OverflowMenuItem[];
  align?: 'left' | 'right';
  label?: string;
}> = ({ items, align = 'right', label = 'More actions' }) => {
  const [open, setOpen] = useState(false);
  // The dropdown is rendered through a portal to document.body so it escapes
  // any `overflow-hidden` / `transform` / `backdrop-blur` ancestor (the
  // PageHeader card clips its children) — otherwise the menu opens but is
  // invisible. Position is measured from the trigger button each time.
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  function place() {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    // Measure the mounted menu when possible; before the first render fall
    // back to roughly its min-width. `left` is always clamped into the
    // viewport so the menu never opens off-screen regardless of which side
    // of the screen the trigger button sits on.
    const width = menuRef.current?.offsetWidth || 220;
    const height = menuRef.current?.offsetHeight || 0;
    let left = align === 'right' ? r.right - width : r.left;
    left = Math.min(Math.max(8, left), window.innerWidth - width - 8);
    let top = r.bottom + 4;
    if (height > 0 && top + height > window.innerHeight - 8) {
      // Not enough room below — open upward instead.
      top = Math.max(8, r.top - height - 4);
    }
    setPos({ top, left });
  }

  useEffect(() => {
    if (!open) return;
    // Re-place now that the menu is mounted and its real size is measurable.
    place();
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onReflow = () => place();
    window.addEventListener('click', onClick);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      window.removeEventListener('click', onClick);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div className="relative inline-block">
      <button
        ref={btnRef}
        type="button"
        onClick={() => {
          if (!open) place();
          setOpen((v) => !v);
        }}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-surface text-ink-muted shadow-sm transition-colors hover:bg-surface-raised hover:text-ink"
      >
        <MoreHorizontal className="h-4 w-4" strokeWidth={2} />
      </button>
      {open && pos &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ position: 'fixed', top: pos.top, left: pos.left }}
            className="app-shell-surface z-[120] min-w-[200px] overflow-hidden rounded-[22px] border border-border py-1 shadow-[0_16px_36px_-24px_rgba(15,23,42,0.38)] backdrop-blur-xl"
          >
          {items.map((it, i) => {
            if (it.separator) {
              return <div key={i} className="my-1 h-px bg-border" />;
            }
            const Icon = it.icon;
            const body = (
              <>
                {Icon && <Icon className="h-4 w-4" strokeWidth={2} />}
                <span className="flex-1 text-left">{it.label}</span>
              </>
            );
            const common = cls(
              'flex w-full min-h-[44px] items-center gap-2 px-3 py-2 text-small transition-colors',
              it.disabled
                ? 'cursor-not-allowed text-ink-muted'
                : it.danger
                ? 'text-neg hover:bg-red-50'
                : 'text-ink hover:bg-surface-raised',
            );
            if (it.href) {
              return (
                <a
                  key={i}
                  role="menuitem"
                  href={it.href}
                  download={it.download}
                  target={it.target}
                  rel={it.target === '_blank' ? 'noopener' : undefined}
                  className={common}
                  onClick={() => setOpen(false)}
                >
                  {body}
                </a>
              );
            }
            return (
              <button
                key={i}
                type="button"
                role="menuitem"
                disabled={it.disabled}
                className={common}
                onClick={() => {
                  setOpen(false);
                  it.onClick?.();
                }}
              >
                {body}
              </button>
            );
          })}
          </div>,
          document.body,
        )}
    </div>
  );
};
